// src/services/accessibilityService.ts
import { logger } from '../utils/logger';
// Service for managing accessibility preferences

import { supabase } from './supabase';

export type ColorblindTheme = 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia';

export interface AccessibilityPreferences {
  reducedMotion: boolean;
  enhancedFocus: boolean;
  highContrast: boolean;
  verboseScreenReader: boolean;
  largerTouchTargets: boolean;
  simplifiedNavigation: boolean;
  colorblindTheme: ColorblindTheme;
}

export const defaultAccessibilityPreferences: AccessibilityPreferences = {
  reducedMotion: false,
  enhancedFocus: false,
  highContrast: false,
  verboseScreenReader: false,
  largerTouchTargets: false,
  simplifiedNavigation: false,
  colorblindTheme: 'none',
};

/**
 * Get accessibility preferences for the current user
 */
export async function getAccessibilityPreferences(): Promise<AccessibilityPreferences> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return defaultAccessibilityPreferences;

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('accessibility_preferences')
      .eq('id', user.id)
      .single();

    if (error) {
      logger.error('Error fetching accessibility preferences:', error);
      return defaultAccessibilityPreferences;
    }

    return {
      ...defaultAccessibilityPreferences,
      ...(profile?.accessibility_preferences || {}),
    };
  } catch (error) {
    logger.error('Error in getAccessibilityPreferences:', error);
    return defaultAccessibilityPreferences;
  }
}

/**
 * Update accessibility preferences for the current user
 */
export async function updateAccessibilityPreferences(
  preferences: Partial<AccessibilityPreferences>
): Promise<{ success: boolean; error?: any }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    // Get current preferences
    const currentPreferences = await getAccessibilityPreferences();

    // Merge with new preferences
    const updatedPreferences = {
      ...currentPreferences,
      ...preferences,
    };

    const { error } = await supabase
      .from('profiles')
      .update({ accessibility_preferences: updatedPreferences })
      .eq('id', user.id);

    if (error) {
      logger.error('Error updating accessibility preferences:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in updateAccessibilityPreferences:', error);
    return { success: false, error };
  }
}

/**
 * Toggle a specific accessibility preference
 */
export async function toggleAccessibilityPreference(
  key: keyof AccessibilityPreferences
): Promise<{ success: boolean; newValue: boolean; error?: any }> {
  try {
    const currentPreferences = await getAccessibilityPreferences();
    const newValue = !currentPreferences[key];

    const result = await updateAccessibilityPreferences({
      [key]: newValue,
    });

    return {
      ...result,
      newValue,
    };
  } catch (error) {
    logger.error('Error in toggleAccessibilityPreference:', error);
    return { success: false, newValue: false, error };
  }
}
